import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DownloadIcon,
  Loader2Icon,
  RefreshCwIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HelpCircleIcon,
  XIcon,
  SearchIcon,
  SparklesIcon,
  CalendarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  UsersIcon,
} from 'lucide-react';
import { useAeoReport } from '../contexts/AeoReportContext';
import ReportSelector from './aeo/ReportSelector';
import CompetitiveLandscape from './aeo/CompetitiveLandscape';
import { toast } from 'react-toastify';
import { SiOpenai, SiGooglegemini, SiGithubcopilot, SiPerplexity, SiMeta, SiClaude } from 'react-icons/si';
import { tenantApi } from '../lib/tenant-api';
import PageLoader from './common/PageLoader';
import AIInsightsDrawer from './AIInsightsDrawer';
import DashboardAssistantBar from './DashboardAssistantBar';
import type {
  AIAuthorityReportItem,
  AIAuthorityTrustSignal,
  AIAuthorityPlatformRow,
  AIAuthorityCompetitor,
} from '../lib/api-types';

const GrokIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100" height="100" viewBox="0 0 48 48"
    fill="#FFFFFF" className={className}>
    <path d="M18.542 30.532l15.956-11.776c.783-.576 1.902-.354 2.274.545 1.962 4.728 1.084 10.411-2.819 14.315-3.903 3.901-9.333 4.756-14.299 2.808l-5.423 2.511c7.778 5.315 17.224 4 23.125-1.903 4.682-4.679 6.131-11.058 4.775-16.812l.011.011c-1.966-8.452.482-11.829 5.501-18.735C47.759 1.332 47.88 1.166 48 1l-6.602 6.599V7.577l-22.86 22.958M15.248 33.392c-5.582-5.329-4.619-13.579.142-18.339 3.521-3.522 9.294-4.958 14.331-2.847l5.412-2.497c-.974-.704-2.224-1.46-3.659-1.994-6.478-2.666-14.238-1.34-19.505 3.922C6.904 16.701 5.31 24.488 8.045 31.133c2.044 4.965-1.307 8.48-4.682 12.023C2.164 44.411.967 45.67 0 47l15.241-13.608"></path>
  </svg>
);

const DefaultIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100" height="100" viewBox="0 0 30 30"
    fill="#FFFFFF" className={className}>
    <path d="M14.217,19.707l-1.112,2.547c-0.427,0.979-1.782,0.979-2.21,0l-1.112-2.547c-0.99-2.267-2.771-4.071-4.993-5.057	L1.73,13.292c-0.973-0.432-0.973-1.848,0-2.28l2.965-1.316C6.974,8.684,8.787,6.813,9.76,4.47l1.126-2.714	c0.418-1.007,1.81-1.007,2.228,0L14.24,4.47c0.973,2.344,2.786,4.215,5.065,5.226l2.965,1.316c0.973,0.432,0.973,1.848,0,2.28	l-3.061,1.359C16.988,15.637,15.206,17.441,14.217,19.707z"></path><path d="M24.481,27.796l-0.339,0.777c-0.248,0.569-1.036,0.569-1.284,0l-0.339-0.777c-0.604-1.385-1.693-2.488-3.051-3.092	l-1.044-0.464c-0.565-0.251-0.565-1.072,0-1.323l0.986-0.438c1.393-0.619,2.501-1.763,3.095-3.195l0.348-0.84	c0.243-0.585,1.052-0.585,1.294,0l0.348,0.84c0.594,1.432,1.702,2.576,3.095,3.195l0.986,0.438c0.565,0.251,0.565,1.072,0,1.323	l-1.044,0.464C26.174,25.308,25.085,26.411,24.481,27.796z"></path>
  </svg>
);

const DeepSeekIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100" height="100" viewBox="0,0,256,256" className={className}>
    <g fill="#ffffff" fill-rule="nonzero" stroke="none" stroke-width="1" stroke-linecap="butt" stroke-linejoin="miter" stroke-miterlimit="10" stroke-dasharray="" stroke-dashoffset="0" font-family="none" font-weight="none" font-size="none" text-anchor="none" mix-blend-mode="normal"><g transform="scale(5.12,5.12)"><path d="M49.475,10.462c-0.53,-0.26 -0.758,0.235 -1.067,0.486c-0.106,0.081 -0.196,0.186 -0.285,0.284c-0.774,0.827 -1.678,1.37 -2.86,1.305c-1.727,-0.097 -3.202,0.446 -4.506,1.767c-0.277,-1.629 -1.198,-2.602 -2.599,-3.226c-0.733,-0.324 -1.475,-0.649 -1.988,-1.354c-0.359,-0.502 -0.456,-1.062 -0.636,-1.613c-0.114,-0.332 -0.228,-0.673 -0.611,-0.73c-0.416,-0.065 -0.579,0.284 -0.742,0.576c-0.652,1.192 -0.904,2.505 -0.88,3.834c0.057,2.991 1.32,5.374 3.83,7.068c0.285,0.195 0.359,0.389 0.269,0.673c-0.171,0.584 -0.375,1.151 -0.554,1.735c-0.114,0.373 -0.285,0.454 -0.684,0.292c-1.377,-0.575 -2.567,-1.427 -3.618,-2.456c-1.784,-1.727 -3.398,-3.631 -5.41,-5.123c-0.473,-0.349 -0.945,-0.673 -1.434,-0.981c-2.053,-1.994 0.269,-3.631 0.807,-3.826c0.562,-0.203 0.196,-0.9 -1.622,-0.892c-1.817,0.008 -3.479,0.616 -5.598,1.427c-0.31,0.122 -0.636,0.211 -0.97,0.284c-1.923,-0.365 -3.919,-0.446 -6.005,-0.211c-3.927,0.438 -7.064,2.294 -9.37,5.463c-2.77,3.81 -3.422,8.138 -2.624,12.653c0.839,4.758 3.267,8.697 6.999,11.778c3.87,3.193 8.328,4.758 13.412,4.458c3.088,-0.178 6.527,-0.592 10.405,-3.874c0.978,0.486 2.004,0.681 3.707,0.827c1.312,0.122 2.575,-0.065 3.553,-0.267c1.532,-0.324 1.426,-1.743 0.872,-2.002c-3.849,-1.347 -4.4,-1.929 -4.4,-1.929c2.281,-2.699 5.72,-5.504 7.065,-14.59c0.106,-0.721 0.016,-1.175 0,-1.759c-0.008,-0.357 0.073,-0.494 0.481,-0.535c1.124,-0.13 2.216,-0.438 3.219,-0.989c2.909,-1.589 4.082,-4.199 4.359,-7.327c0.04,-0.481 -0.009,-0.975 -0.515,-1.226zM24.126,38.621c-4.351,-3.42 -6.461,-4.547 -7.333,-4.499c-0.815,0.049 -0.668,0.981 -0.489,1.589c0.187,0.6 0.432,1.013 0.774,1.54c0.236,0.349 0.399,0.867 -0.236,1.256c-1.402,0.867 -3.838,-0.292 -3.952,-0.349c-2.835,-1.67 -5.207,-3.875 -6.877,-6.89c-1.613,-2.902 -2.55,-6.014 -2.705,-9.338c-0.041,-0.802 0.196,-1.086 0.994,-1.232c1.051,-0.195 2.135,-0.235 3.186,-0.081c4.441,0.649 8.222,2.634 11.391,5.779c1.809,1.791 3.178,3.931 4.588,6.023c1.499,2.221 3.113,4.337 5.166,6.071c0.725,0.608 1.304,1.07 1.858,1.41c-1.671,0.188 -4.458,0.228 -6.365,-1.279zM26.212,25.206c0,-0.357 0.285,-0.64 0.644,-0.64c0.081,0 0.155,0.016 0.22,0.04c0.09,0.033 0.171,0.081 0.236,0.154c0.114,0.114 0.179,0.276 0.179,0.446c0,0.357 -0.285,0.64 -0.644,0.64c-0.359,0 -0.635,-0.284 -0.635,-0.64zM32.69,28.529c-0.416,0.17 -0.831,0.316 -1.23,0.332c-0.619,0.032 -1.296,-0.219 -1.662,-0.527c-0.57,-0.478 -0.978,-0.746 -1.149,-1.581c-0.073,-0.357 -0.033,-0.908 0.033,-1.224c0.147,-0.681 -0.016,-1.119 -0.497,-1.516c-0.391,-0.324 -0.888,-0.413 -1.434,-0.413c-0.204,0 -0.391,-0.089 -0.53,-0.162c-0.228,-0.113 -0.415,-0.397 -0.236,-0.746c0.057,-0.113 0.334,-0.389 0.399,-0.438c0.742,-0.422 1.597,-0.284 2.387,0.032c0.733,0.3 1.287,0.851 2.086,1.629c0.815,0.94 0.961,1.2 1.426,1.905c0.367,0.551 0.701,1.119 0.929,1.767c0.138,0.407 -0.041,0.74 -0.522,0.942z"></path></g></g>
  </svg>
);

// ── Helpers ──────────────────────────────────────────────────────────

const PLATFORM_ICON_CONFIG: Record<string, { icon: React.ReactNode; bg: string }> = {
  ChatGPT: { icon: <SiOpenai className="h-5 w-5 text-white" />, bg: 'bg-[#000000]' },
  Gemini: { icon: <SiGooglegemini className="h-5 w-5 text-white" />, bg: 'bg-[#161463]' },
  Claude: { icon: <SiClaude className="h-5 w-5 text-white" />, bg: 'bg-[#D97757]' },
  Perplexity: { icon: <SiPerplexity className="h-5 w-5 text-white" />, bg: 'bg-[#267E88]' },
  Copilot: { icon: <SiGithubcopilot className="h-5 w-5 text-white" />, bg: 'bg-[#000000]' },
  DeepSeek: { icon: <DeepSeekIcon className="h-5 w-5 text-white" />, bg: 'bg-[#4D6BFE]' },
  Grok: { icon: <GrokIcon className="h-5 w-5 text-white" />, bg: 'bg-[#000000]' },
  'Meta AI': { icon: <SiMeta className="h-5 w-5 text-white" />, bg: 'bg-[#4D6BFE]' },
  'Google AI': { icon: <DefaultIcon className="h-5 w-5 text-white" />, bg: 'bg-[#000000]' },
};

export const PlatformIcon: React.FC<{ platform: string }> = ({ platform }) => {
  const config = PLATFORM_ICON_CONFIG[platform];
  if (!config) return <div className="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center"><DefaultIcon className="h-4 w-4 text-white" /></div>;
  return <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>{config.icon}</div>;
};

function gradeTrustColor(grade: string): string {
  const letter = grade.charAt(0).toUpperCase();
  if (letter === 'A') return `bg-[#F2F8F5] border-[#D6E9DF]`;
  if (letter === 'B') return `bg-[#F3F5FA] border-[#D9E1F2]`;
  if (letter === 'C') return `bg-[#FBF7F0] border-[#F0E4CC]`;
  if (letter === 'F') return `bg-[#FDF2F2] border-[#F2D6D6]`;
  return `bg-[#F5F6FA]`;
}

function gradeColor(grade: string): string {
  const letter = grade.charAt(0).toUpperCase();
  if (letter === 'A') return `bg-[#161463] text-white`;
  if (letter === 'B') return `bg-[#161463] text-white`;
  if (letter === 'C') return `bg-[#161463] text-white`;
  if (letter === 'D') return `bg-[#161463] text-white`;
  return `bg-[#161463] text-white`; // F
}

function gradePillColor(grade: string): string {
  const letter = grade.charAt(0).toUpperCase();
  if (letter === 'A') return `bg-[#161463] text-white`;
  if (letter === 'B') return `bg-[#161463] text-white`;
  if (letter === 'C') return `bg-[#161463] text-white`;
  if (letter === 'D') return `bg-[#161463] text-white`;
  return `bg-[#161463] text-white`;
}

function sentimentBadge(s: string) {
  if (s === 'Positive') return { className: 'bg-[#B9FFEF61] text-[#006D54] border-[#006D54] rounded', label: 'Positive' };
  if (s === 'Neutral') return { className: 'bg-[#F1F1F1] text-[#8D8D8D] border-[#8D8D8D] rounded', label: 'Neutral' };
  return { className: 'bg-[#FFE3E3] text-[#C00000] border-[#C00000] rounded', label: 'Weak' };
}

// ── Pagination helper ────────────────────────────────────────────────

function usePagination<T>(items: T[], perPage: number) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / perPage));
  const slice = useMemo(() => items.slice((page - 1) * perPage, page * perPage), [items, page, perPage]);
  return { page, totalPages, slice, setPage };
}

// ── Sub-components ───────────────────────────────────────────────────

const Paginator: React.FC<{ page: number; totalPages: number; setPage: (p: number) => void }> = ({ page, totalPages, setPage }) => (
  <div className="flex items-center justify-end gap-1 mt-4">
    <button disabled={page === 1} onClick={() => setPage(page - 1)} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronLeftIcon className="h-4 w-4" /></button>
    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
      <button key={p} onClick={() => setPage(p)} className={`w-6 h-6 rounded border text-xs font-medium  ${p === page ? 'border-[#8C9DD1] bg-[#161463] text-white' : 'border-[#EEEEEE] bg-[#F5F5F5] text-gray-600 hover:bg-gray-200'}`}>{p}</button>
    ))}
    <button disabled={page === totalPages} onClick={() => setPage(page + 1)} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronRightIcon className="h-4 w-4" /></button>
  </div>
);

/**
 * Delta vs the direct previous report. Renders an up/down arrow with the change
 * amount; nothing when either value is missing or the change rounds to zero.
 */
const TrendIndicator: React.FC<{ current?: number | null; previous?: number | null; className?: string }> = ({ current, previous, className = '' }) => {
  // Need both the current value and a previous report to compute a delta.
  if (current === null || current === undefined || previous === null || previous === undefined) return null;
  const delta = Math.round((current - previous) * 10) / 10;
  if (delta === 0) {
    return (
      <span className={`inline-flex items-center text-[11px] font-medium text-gray-300 ${className}`} title="No change vs previous report">—</span>
    );
  }
  const up = delta > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? 'text-green-600' : 'text-red-500'} ${className}`}
      title={`${up ? '+' : ''}${delta} vs previous report`}
    >
      {up ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />}
      {up ? '+' : ''}{delta}
    </span>
  );
};

const TrustSignalCard: React.FC<{ signal: AIAuthorityTrustSignal; onClick?: () => void; previousScore?: number }> = ({ signal, onClick, previousScore }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className={`group text-left w-full border ${gradeTrustColor(signal.grade)} rounded-2xl p-4 flex flex-col items-center text-center min-w-0 transition-all ${onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-[#161463] focus:outline-none focus:ring-2 focus:ring-[#161463] focus:ring-offset-1' : 'cursor-default'}`}
  >
    <div className='flex items-center gap-2 w-full'>
      <PlatformIcon platform={signal.platform} />
      <h4 className="font-semibold text-gray-900 text-sm">{signal.platform}</h4>
    </div>
    <span className={`mt-3 inline-block px-4 py-1.5 rounded-full text-xs font-bold w-full ${gradePillColor(signal.grade)}`}>
      Authority: {signal.score ?? signal.grade}
    </span>
    {signal.score != null && previousScore != null && (
      <TrendIndicator current={signal.score} previous={previousScore} className="mt-1.5" />
    )}
    <p className="mt-3 text-xs text-gray-500 text-left">{signal.description}</p>
    {onClick && (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#161463] transition-opacity mt-auto pt-3">
        View deep dive <ChevronRightIcon className="h-3 w-3" />
      </span>
    )}
  </button>
);

// ── Column descriptions ──────────────────────────────────────────────

const COLUMN_DESCRIPTIONS: Record<string, { title: string; description: string }> = {
  authority: {
    title: 'Authority',
    description: 'How often and how prominently your brand appears in AI-generated answers to relevant questions.',
  },
  visibility: {
    title: 'Visibility',
    description: 'How accurately and confidently AI systems describe your brand based on your documented positioning.',
  },
  conversion: {
    title: 'Conversion',
    description: 'How strongly AI answers position your brand as a recommended solution to the user’s intent.',
  },
  competitive_share: {
    title: 'Competitive Share',
    description: 'The proportion and prominence of brand mentions compared to competitors in relevant AI responses.',
  },
  sentiment: {
    title: 'Sentiment',
    description: 'The tone and overall favorability of how AI systems reference your brand.',
  },
};

const InfoPopover: React.FC<{
  columnKey: string;
  isOpen: boolean;
  onToggle: (key: string) => void;
  align?: 'center' | 'right';
}> = ({ columnKey, isOpen, onToggle, align = 'center' }) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const info = COLUMN_DESCRIPTIONS[columnKey];

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onToggle(columnKey);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, columnKey, onToggle]);

  if (!info) return null;

  return (
    <span className="relative inline-flex ml-1">
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(columnKey); }}
        className="text-[#161463] hover:text-[#0077CC] cursor-pointer transform translate-y-[2px]"
      >
        <HelpCircleIcon className="h-3.5 w-3.5" />
      </button>

      {isOpen && (
        <div ref={popoverRef} className={`absolute top-6 z-50 w-80 bg-white  shadow-lg border border-gray-200 p-4 ${align === 'right' ? 'right-0' : 'left-1/2 -translate-x-1/2'}`} onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between mb-2">
            <h4 className="font-bold text-gray-900 text-sm">{info.title}</h4>
            <button onClick={() => onToggle(columnKey)} className="text-gray-400 hover:text-gray-600 -mt-0.5">
              <XIcon className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed mb-3">{info.description}</p>
          <a href="#" className="text-xs font-medium text-gray-900 underline underline-offset-2 hover:text-gray-700">
            See how LAWMA is working to improve this
          </a>
        </div>
      )}
    </span>
  );
};

// ── AI Assistant suggestions ─────────────────────────────────────────

const AI_AUTHORITY_SUGGESTIONS = [
  'Which AI platforms is my brand weakest on?',
  'How does my AI visibility compare to competitors?',
  'What top 3 actions improve my AI authority?',
];

// Shared gradient for "Analyze" CTAs (deep navy → periwinkle).
const ANALYZE_GRADIENT = 'bg-gradient-to-r from-[#161463] to-[#5457A6]';

// ── Main Component ───────────────────────────────────────────────────

const AIAuthority: React.FC = () => {
  const navigate = useNavigate();
  const { data, refetch, isFetching } = useQuery({
    queryKey: ['ai-authority'],
    queryFn: () => tenantApi.getAIAuthority(),
    retry: false,
  });

  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const [showCompetitorsModal, setShowCompetitorsModal] = useState(false);
  const handleTooltipToggle = useCallback((key: string) => setOpenTooltip((prev) => (prev === key ? null : key)), []);

  // Selected report id (persisted across the summary ↔ deep-dive navigation).
  const { selectedReportId, setSelectedReportId } = useAeoReport();

  // AI Assistant chat (ai_authority context)
  const [showAIChat, setShowAIChat] = useState(false);
  const [initialAIMessage, setInitialAIMessage] = useState<string | undefined>(undefined);

  // Open the AI Assistant and auto-send a platform-specific deep-dive prompt.
  const handleAnalyzePlatform = useCallback((platform: string) => {
    setInitialAIMessage(`Give me a deep dive on my AI authority on ${platform}. What's driving the score and what should I do to improve it?`);
    setShowAIChat(true);
  }, []);

  // Open the AI Assistant and auto-send a competitor benchmarking prompt.
  const handleAnalyzeCompetitors = useCallback(() => {
    setInitialAIMessage('How does my AI visibility compare to my competitors, and where are they outperforming me?');
    setShowAIChat(true);
  }, []);

  // Open the AI Assistant and auto-send a free-form question from the composer.
  const handleAssistantSubmit = useCallback((text: string) => {
    setInitialAIMessage(text);
    setShowAIChat(true);
  }, []);

  // Multiple reports — pick the selected one, defaulting to the latest.
  const reports = data?.reports ?? [];
  const selectedId = selectedReportId ?? data?.latest_report_id ?? reports[0]?.lars_report_id ?? null;
  const d: AIAuthorityReportItem | undefined = reports.find((r) => r.lars_report_id === selectedId) ?? reports[0];

  // Direct previous report (next older in the newest-first list) for trend deltas.
  const currentIndex = d ? reports.indexOf(d) : -1;
  const prevReport = currentIndex >= 0 ? reports[currentIndex + 1] : undefined;
  const prevMatrix = new Map((prevReport?.platform_matrix ?? []).map((r) => [r.platform, r]));
  const prevTrust = new Map((prevReport?.trust_signals ?? []).map((s) => [s.platform, s]));

  // Report id used for the platform deep-dive route (prefer the full-report id).
  const deepDiveReportId = d?.full_report_id ?? d?.lars_report_id ?? null;

  const latestReportId = data?.latest_report_id ?? reports[0]?.lars_report_id ?? null;
  const isViewingLatest = !d || d.lars_report_id === latestReportId;

  // Full report for the current selection — powers the "View all" competitive modal.
  const { data: fullReport, isFetching: fullReportLoading } = useQuery({
    queryKey: ['ai-authority-full', deepDiveReportId],
    queryFn: () => tenantApi.getAIAuthorityFull(deepDiveReportId!),
    enabled: !!deepDiveReportId,
    retry: false,
  });

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadReport = useCallback(async () => {
    if (!d?.lars_run_name || isDownloading) return;
    setIsDownloading(true);
    try {
      let blob: Blob;
      // Prefer the versioned archived PDF when lars_report_id is available,
      // so the download matches exactly the report the client is viewing.
      if (d.lars_report_id) {
        try {
          blob = await tenantApi.downloadLarsReportById(d.lars_run_name, d.lars_report_id);
        } catch {
          // Archived PDF not available (pre-versioning report) — fall back to latest
          blob = await tenantApi.downloadLarsReport(d.lars_run_name);
        }
      } else {
        blob = await tenantApi.downloadLarsReport(d.lars_run_name);
      }
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = `lars_report_${d.lars_report_id ?? d.lars_run_name}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to download report. Please try again.';
      toast.error(msg);
    } finally {
      setIsDownloading(false);
    }
  }, [d?.lars_run_name, d?.lars_report_id, isDownloading]);

  const trustPagination = usePagination<AIAuthorityTrustSignal>(d?.trust_signals ?? [], 4);
  const matrixPagination = usePagination<AIAuthorityPlatformRow>(d?.platform_matrix ?? [], 4);

  // Show full-screen loader only on the very first load (no cached data yet)
  if (data === undefined && isFetching) {
    return <PageLoader fullScreen={false} />;
  }

  if (!d?.lars_run_name) {
    return (
      <div className="bg-white shadow-sm border border-gray-100 overflow-hidden mt-4">
        <div className="h-1.5 w-full bg-gradient-to-r from-purple-500 to-violet-400" />
        <div className="px-8 py-14 flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-violet-400 flex items-center justify-center shadow-lg mb-6">
            <SearchIcon className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            Answer Engine Optimization
          </h1>
          <p className="mt-4 text-base text-gray-500 max-w-xl leading-relaxed">
            Your system is being optimized, come back later to view your results
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white text-sm font-semibold shadow-md hover:opacity-90 transition-opacity"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* pb-36 keeps the last row scrollable clear of the fixed assistant bar */}
      <div className="space-y-4 mt-4 pb-36">
        {/* Page Header */}
        <div className="pb-4 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">LAWMA Answer Rank Scorecard (LARS) Dashboard</h1>
            <p className="mt-1 text-sm text-gray-500">
              How confidently AI systems reference and rely on your answers
            </p>
          </div>
          <div className="flex flex-col items-end gap-2.5 flex-shrink-0">
            <div className='flex items-center gap-2'>
              <div className="inline-flex items-center gap-2 border border-gray-300 rounded-full px-4 py-2 bg-white">
                <span className="text-sm font-semibold text-[#0F3659]">Status:</span>
                <span className="text-sm text-[#0F3659]">{(d as any).status ?? 'Learning'}</span>
                <div className='w-4 h-4 p-[3px] rounded-full bg-amber-200 flex-shrink-0'>
                  <div className="w-full h-full rounded-full bg-[#05004E] flex-shrink-0 animate-pulse" ></div>
                </div>
              </div>
              <HelpCircleIcon className="h-4 w-4 text-[#000000] flex-shrink-0 font-medium" />
            </div>
            {(d as any).website_being_analyzed && (
              <p className="text-sm text-[#8181A5]">
                Website Being Analyzed:{' '}
                <span className="font-semibold text-[#000000]">{(d as any).website_being_analyzed}</span>
              </p>
            )}
          </div>
        </div>

        {/* Report selector — switch between synced LARS reports */}
        {reports.length > 1 && (
          <div className="flex justify-start sm:justify-end border-t border-gray-100 pt-4">
            <div className="flex items-center flex-wrap gap-2 text-sm text-[#8181A5]">
              {!isViewingLatest && (
                <>
                  <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs font-medium">Older report</span>
                  <button onClick={() => setSelectedReportId(null)} className="text-xs font-semibold text-[#161463] hover:underline">
                    View latest
                  </button>
                </>
              )}
              <CalendarIcon className="h-4 w-4" />
              <ReportSelector
                reports={reports}
                current={d}
                latestId={data?.latest_report_id}
                onSelect={(r) => setSelectedReportId(r.lars_report_id ?? null)}
              />
            </div>
          </div>
        )}

        <div key={selectedId ?? 'default'} className="space-y-4 animate-fade-in-up">
          {/* Row 1 — Authority Level + Trust Signals */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start xl:items-stretch">
            {/* AI Authority Level */}
            <div className="bg-white  border border-gray-100 px-6 py-8 flex flex-col items-center text-center">
              <h2 className="text-xl font-bold text-[#05004E] mb-5">AI Authority Level</h2>

              {/* Grade badge */}
              <div className={`w-full max-w-[260px]  py-4 ${gradeColor(d.overall_grade)} rounded-3xl flex items-center justify-center`}>
                <span className="text-4xl font-black">{d.overall_score ?? d.overall_grade}</span>
              </div>

              <div className="mt-5 flex items-center gap-2">
                <div className="h-5 flex items-center justify-center gap-1.5">
                  <TrendIndicator current={d.overall_score} previous={prevReport?.overall_score} className="text-sm" />
                  {prevReport && d.overall_score != null && prevReport.overall_score != null && d.overall_score !== prevReport.overall_score && (
                    <span className="text-xs text-gray-400">vs previous report</span>
                  )}
                </div>

                <span className="font-semibold text-[#05004E]">{d.overall_label}</span>
              </div>
              <p className="mt-1 text-sm text-gray-600">{d.overall_trend}</p>

              <button
                onClick={handleDownloadReport}
                disabled={!d.lars_run_name || isDownloading}
                title={!d.lars_run_name ? 'No LARS report available yet' : 'Download PDF report'}
                className="mt-6 w-full max-w-[260px] inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloading
                  ? <Loader2Icon className="h-4 w-4 animate-spin" />
                  : <DownloadIcon className="h-4 w-4" />
                }
                {isDownloading ? 'Downloading...' : 'View Detailed Report'}
              </button>
            </div>

            {/* Trust Signals */}
            <div className="lg:col-span-2 bg-white  border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-[#05004E]">Trust Signals</h2>
              <p className="mt-1 text-sm text-gray-500">
                <span className="font-semibold text-gray-600">Platforms In Comparison:</span>{' '}
                {d.platforms_in_comparison.join(', ')}
              </p>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {trustPagination.slice.map((s) => (
                  <TrustSignalCard
                    key={s.platform}
                    signal={s}
                    previousScore={prevTrust.get(s.platform)?.score}
                    onClick={deepDiveReportId ? () => navigate(`/dashboard/aeo/${encodeURIComponent(deepDiveReportId)}/${encodeURIComponent(s.platform)}-deep-dive`) : undefined}
                  />
                ))}
              </div>

              <Paginator page={trustPagination.page} totalPages={trustPagination.totalPages} setPage={trustPagination.setPage} />
            </div>
          </div>

          {/* Row 2 — Platform Matrix + Competitive Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Platform Authority Matrix */}
            <div className="lg:col-span-2 bg-white  border border-gray-100 p-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-lg font-bold text-[#05004E]">Platform Authority Matrix</h2>
                <div className="text-right text-xs text-gray-500">
                  <p>Last Synced: {d.last_synced}</p>
                  <button onClick={() => refetch()} disabled={isFetching} className="inline-flex items-center gap-1 text-[#161463] font-medium mt-0.5 disabled:opacity-50 underline">
                    <RefreshCwIcon className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2.5 pr-4 font-medium text-[#8181A5]">Platform</th>
                      <th className="text-left py-2.5 pr-4 font-medium text-[#8181A5] relative">Authority<InfoPopover columnKey="authority" isOpen={openTooltip === 'authority'} onToggle={handleTooltipToggle} /></th>
                      <th className="text-left py-2.5 pr-4 font-medium text-[#8181A5] relative">Visibility<InfoPopover columnKey="visibility" isOpen={openTooltip === 'visibility'} onToggle={handleTooltipToggle} /></th>
                      <th className="text-left py-2.5 pr-4 font-medium text-[#8181A5] relative">Conversion<InfoPopover columnKey="conversion" isOpen={openTooltip === 'conversion'} onToggle={handleTooltipToggle} /></th>
                      <th className="text-left py-2.5 pr-4 font-medium text-[#8181A5] relative">Competitive Share<InfoPopover columnKey="competitive_share" isOpen={openTooltip === 'competitive_share'} onToggle={handleTooltipToggle} align="right" /></th>
                      <th className="text-left py-2.5 pr-4 font-medium text-[#8181A5] relative">Sentiment<InfoPopover columnKey="sentiment" isOpen={openTooltip === 'sentiment'} onToggle={handleTooltipToggle} align="right" /></th>
                      <th className="text-left py-2.5 font-medium text-[#8181A5]">Platform Deep Dive</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {matrixPagination.slice.map((row) => {
                      const badge = sentimentBadge(row.sentiment);
                      const prev = prevMatrix.get(row.platform);
                      return (
                        <tr key={row.platform} className="hover:bg-gray-50 text-xs">
                          <td className="py-3 pr-4 font-medium text-[#292D32]">{row.platform}</td>
                          <td className="py-3 pr-4 font-medium text-[#292D32]">
                            <div className="flex items-center gap-1.5">
                              <span>{row.authority}</span>
                              <TrendIndicator current={row.authority_score} previous={prev?.authority_score} />
                            </div>
                          </td>
                          <td className="py-3 pr-4 font-medium text-[#292D32]">
                            <div className="flex items-center gap-1.5">
                              <span>{row.visibility}</span>
                              <TrendIndicator current={row.visibility} previous={prev?.visibility} />
                            </div>
                          </td>
                          <td className="py-3 pr-4 font-medium text-[#292D32]">
                            <div className="flex items-center gap-1.5">
                              <span>{row.conversion}</span>
                              <TrendIndicator current={row.conversion} previous={prev?.conversion} />
                            </div>
                          </td>
                          <td className="py-3 pr-4 font-medium text-[#292D32]">
                            <div className="flex items-center gap-1.5">
                              <span>{row.competitive_share}</span>
                              <TrendIndicator current={row.competitive_share} previous={prev?.competitive_share} />
                            </div>
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`inline-flex items-center justify-center px-2.5 py-0.5 text-xs font-semibold w-20  border ${badge.className}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="py-3">
                            <button
                              onClick={() => handleAnalyzePlatform(row.platform)}
                              title={`Analyze ${row.platform} with AI`}
                              className={`inline-flex items-center rounded gap-1.5 px-3 py-1.5 text-xs font-medium text-white ${ANALYZE_GRADIENT} hover:opacity-90 transition-opacity`}
                            >
                              <SparklesIcon className="h-3 w-3" /> Analyze
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="mt-3 text-sm text-gray-500">
                Showing data from {matrixPagination.slice.length} of {d.platform_matrix.length} platforms
              </p>

              <Paginator page={matrixPagination.page} totalPages={matrixPagination.totalPages} setPage={matrixPagination.setPage} />
            </div>

            {/* Competitive Comparison */}
            <div className="bg-white  border border-gray-100">
              <div className="flex items-center justify-between p-6">
                <h2 className="text-lg font-bold text-[#05004E]">Competitive Comparison</h2>
                <button
                  onClick={() => setShowCompetitorsModal(true)}
                  className="group inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold text-[#161463] bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors shrink-0"
                >
                  View all
                  <ChevronRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>

              {/* Header */}
              <div className="flex items-center justify-between text-sm font-semibold text-[#8181A5] capitalize tracking-wider p-6 py-4 bg-gray-50">
                <span>Competitor</span>
                <span>LARS Grade</span>
              </div>

              <div>
                {d.competitors.map((c: AIAuthorityCompetitor) => (
                  <div key={c.name} className="flex items-center justify-between p-6 py-4">
                    <span className="text-sm font-medium text-gray-900">{c.name}</span>
                    <span className={`inline-flex items-center justify-center w-14 h-7 rounded-full text-xs font-bold ${gradePillColor(c.grade)}`}>
                      {c.score ?? c.grade}
                    </span>
                  </div>
                ))}
              </div>

              <div className="p-6 pt-2">
                <button
                  onClick={handleAnalyzeCompetitors}
                  title="Analyze competitors with AI"
                  className={`w-full inline-flex items-center rounded justify-center gap-1.5 px-4 py-3 text-sm font-medium text-white ${ANALYZE_GRADIENT} hover:opacity-90 transition-opacity`}
                >
                  <SparklesIcon className="h-4 w-4" /> Analyze
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Dashboard assistant composer — fixed bottom center */}
      <DashboardAssistantBar
        onSubmit={handleAssistantSubmit}
        onOpen={() => setShowAIChat(true)}
        suggestions={AI_AUTHORITY_SUGGESTIONS}
      />

      {/* AI Insights Drawer — ai_authority context */}
      <AIInsightsDrawer
        isOpen={showAIChat}
        onClose={() => { setShowAIChat(false); setInitialAIMessage(undefined); }}
        contextKey="ai_authority"
        description="AI Authority context"
        title="Ask me anything about your AI authority"
        subtitle="Get insights grounded in your latest LARS report and your own content."
        suggestions={AI_AUTHORITY_SUGGESTIONS}
        initialMessage={initialAIMessage}
      />

      {/* Competitive landscape modal — full report for the current selection */}
      {showCompetitorsModal && (
        <div
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
          onClick={() => setShowCompetitorsModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-3xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <UsersIcon className="h-5 w-5 text-[#161463]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Competitive landscape</h3>
                  <p className="text-sm text-gray-500">How your brand stacks up against competitors</p>
                </div>
              </div>
              <button onClick={() => setShowCompetitorsModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Close">
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {fullReport?.competitor_comparison ? (
                <CompetitiveLandscape data={fullReport.competitor_comparison} />
              ) : fullReportLoading ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-[#161463] mb-3" />
                  <p className="text-sm">Loading competitors…</p>
                </div>
              ) : (
                <p className="text-center text-sm text-gray-500 py-16">No competitor data available for this report.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAuthority;
